import { Link } from "react-router-dom";
import Box from "../box";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { Chip, AccordionItem, Accordion } from "@heroui/react";
import { useCallback, useMemo } from "react";
import GridWrapper from "/app/src/components/generic/gridWrapper";

type ModelItem = {
  id: string;
  name: string;
  value?: string;
};

interface ModelItemsProps {
  modelName: string;
  items: ModelItem[];
  clickable?: boolean; // if false, no Links, no hover, no external icon
  routeBase?: string; // optional override for the route base (e.g. 'materials' or 'material_properties')
}

function toRouteBase(name: string) {
  // convert 'Material Properties' -> 'material_properties', 'Materials' -> 'materials'
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Renders a list of model items within an expandable accordion and grid layout.
 * @param {Object} props - The component props.
 * @param {string} props.modelName - Name of the model to display.
 * @param {Array} props.items - Array of items associated with the model.
 * @param {boolean} [props.clickable=true] - Whether each item should be clickable.
 * @param {string} [props.routeBase] - Base route path for item links.
 * @returns {JSX.Element} The ModelItems component.
 */
export default function ModelItems({
  modelName,
  items,
  clickable = true,
  routeBase,
}: ModelItemsProps) {
  const toggleIndicatorIcon = useCallback(({ isOpen }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  const base = useMemo(() => {
    return routeBase && routeBase.length > 0
      ? routeBase
      : toRouteBase(modelName);
  }, [modelName, routeBase]);

  const title = (
    <div className="flex items-center gap-2 w-full">
      <div className="flex items-center">
        <h3 className="text-[16px] font-medium mb-0">{modelName}</h3>
      </div>
      <Chip color="default" size="sm">
        {items?.length}
      </Chip>
    </div>
  );

  const itemHoverClass = clickable
    ? "hover:border-primary-surface hover:bg-primary-muted transition-all duration-300"
    : "";

  return (
    <Box>
      <Accordion
        selectionMode="single"
        showDivider
        itemClasses={{ trigger: "py-0" }}
      >
        <AccordionItem
          key={modelName}
          title={title}
          indicator={toggleIndicatorIcon}
        >
          <div className="mt-2">
            <GridWrapper responsive={{ sm: 1, md: 2, lg: 2 }} gap={4}>
              {items?.map((item) => {
                return (
                  <Box
                    key={item.id}
                    className={`relative !mb-0 !px-[12px] !py-[12px] flex items-center justify-between ${itemHoverClass}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <h4
                        className={`text-[14px] ${clickable ? "text-primary-default" : ""} mb-0`}
                      >
                        {item.name}
                      </h4>
                      <div className="flex items-center">
                        <h4 className="text-[14px] text-neutral-smoke mb-0">
                          {item?.value}
                        </h4>
                        {clickable && (
                          <IconBuilder
                            icon="ExternalLink"
                            size={18}
                            className="ml-1"
                          />
                        )}
                      </div>
                    </div>
                    {clickable && (
                      // Absolute full size link so the whole box/card is clickable

                      <Link
                        to={`/explorer/${base}/${item.id}`}
                        className="absolute inset-0 z-10"
                      />
                    )}
                  </Box>
                );
              })}
            </GridWrapper>
          </div>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
